/**
 * CrosshairOverlay - 2D crosshair overlay that can track 3D world positions.
 *
 * Features:
 * - Renders a crosshair at a specified canvas position
 * - Can follow a 3D world position (automatically projects to 2D)
 * - Customizable appearance (color, size, gap)
 * - Interactive hit test for hover detection
 */
import { IViewport } from '../../viewports';
import { IOverlayObject } from '../types';
import { snapToPixel } from './canvas-utils';

export interface CrosshairStyle {
    /** Crosshair color */
    color: string;
    /** Line width in pixels */
    lineWidth: number;
    /** Length of each crosshair line from center */
    size: number;
    /** Gap from center point */
    gap: number;
    /** Whether to draw a center dot */
    showCenterDot: boolean;
    /** Center dot radius */
    centerDotRadius: number;
}

export interface CrosshairOptions {
    /** Unique identifier */
    id: string;
    /** Optional 3D world position to track [x, y, z] */
    worldPosition?: [number, number, number];
    /** Optional fixed canvas position (ignored if worldPosition is set) */
    canvasPosition?: { x: number; y: number };
    /** Visual style */
    style?: Partial<CrosshairStyle>;
    /** Z-order for rendering */
    zIndex?: number;
    /** Hit test radius in pixels (defaults to size) */
    hitRadius?: number;
    /** Whether this overlay is visible (defaults to true) */
    visible?: boolean;
    /** Whether to clip rendering based on camera clipping range (defaults to false) */
    clipByCamera?: boolean;
}

const DEFAULT_STYLE: CrosshairStyle = {
    color: '#00ff00',
    lineWidth: 2,
    size: 20,
    gap: 5,
    showCenterDot: true,
    centerDotRadius: 3,
};

export class CrosshairOverlay implements IOverlayObject {
    readonly id: string;
    readonly interactive = true;
    visible = true;
    readonly zIndex: number;
    readonly clipByCamera: boolean;

    worldPosition: [number, number, number] | null = null;
    canvasPosition: { x: number; y: number } | null = null;
    private style: CrosshairStyle;
    private hitRadius: number;

    constructor(options: CrosshairOptions) {
        this.id = options.id;
        this.visible = options.visible ?? true;
        this.zIndex = options.zIndex ?? 100;
        this.clipByCamera = options.clipByCamera ?? false;
        this.style = { ...DEFAULT_STYLE, ...options.style };
        this.hitRadius = options.hitRadius ?? this.style.size;

        if (options.worldPosition) {
            this.worldPosition = [...options.worldPosition];
        } else if (options.canvasPosition) {
            this.canvasPosition = { ...options.canvasPosition };
        }
    }

    /**
     * Update the world position to track.
     */
    setWorldPosition(position: [number, number, number] | null): void {
        if (position) {
            this.worldPosition = [...position];
        } else {
            this.worldPosition = null;
        }
    }

    /**
     * Update the canvas position (only used if no world position is set).
     */
    setCanvasPosition(position: { x: number; y: number } | null): void {
        this.canvasPosition = position ? { ...position } : null;
    }

    /**
     * Update the visual style.
     */
    updateStyle(style: Partial<CrosshairStyle>): void {
        Object.assign(this.style, style);
    }

    /**
     * Get the current style.
     */
    getStyle(): CrosshairStyle {
        return { ...this.style };
    }

    /**
     * Hit test - returns true if the given canvas coordinates are within
     * the hit radius of the crosshair center.
     */
    hitTest(canvasX: number, canvasY: number): boolean {
        if (!this.canvasPosition) return false;

        const dx = canvasX - this.canvasPosition.x;
        const dy = canvasY - this.canvasPosition.y;
        const distSq = dx * dx + dy * dy;

        return distSq <= this.hitRadius * this.hitRadius;
    }

    /**
     * Called when viewport camera changes.
     * Recalculates canvas position from world position if tracking 3D point.
     */
    onCameraChanged(viewport: IViewport): void {
        if (this.worldPosition) {
            // Project 3D world position to 2D canvas coordinates
            const projected = viewport.worldToCanvas(this.worldPosition);
            if (projected) {
                this.canvasPosition = { x: projected.x, y: projected.y };
            } else {
                // Point is behind camera or out of view
                this.canvasPosition = null;
            }
        }
    }

    /**
     * Render the crosshair to canvas.
     */
    render(ctx: CanvasRenderingContext2D, viewport: IViewport): void {
        // Update projection if tracking world position
        if (this.worldPosition) {
            this.onCameraChanged(viewport);
        }

        // Don't render if no valid position
        if (!this.canvasPosition) {
            return;
        }

        const { color, lineWidth, size, gap, showCenterDot, centerDotRadius } = this.style;
        const x = snapToPixel(this.canvasPosition.x, lineWidth);
        const y = snapToPixel(this.canvasPosition.y, lineWidth);

        ctx.save();
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = lineWidth;
        ctx.lineCap = 'butt';

        // Draw horizontal line
        ctx.beginPath();
        ctx.moveTo(x - size, y);
        ctx.lineTo(x - gap, y);
        ctx.moveTo(x + gap, y);
        ctx.lineTo(x + size, y);
        ctx.stroke();

        // Draw vertical line
        ctx.beginPath();
        ctx.moveTo(x, y - size);
        ctx.lineTo(x, y - gap);
        ctx.moveTo(x, y + gap);
        ctx.lineTo(x, y + size);
        ctx.stroke();

        // Draw center dot
        if (showCenterDot) {
            ctx.beginPath();
            ctx.arc(x, y, centerDotRadius, 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();
    }
}
